import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SlidersHorizontal, X, MapPin, ChevronDown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import SearchWidget from '../components/SearchWidget';
import { propertiesAPI } from '../lib/api';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'homestay', label: 'Homestay' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'pg', label: 'PG' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'farmhouse', label: 'Farmhouse' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'function_hall', label: 'Function Hall' },
  { value: 'banquet_hall', label: 'Banquet Hall' },
];

const AMENITY_OPTIONS = ['pool', 'wifi', 'parking', 'bbq', 'ac', 'music', 'gym', 'kitchen'];

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const navigate = useNavigate();

  const experience = searchParams.get('experience') || '';
  const city = searchParams.get('city') || '';
  const guests = searchParams.get('guests') || '';

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const data = await propertiesAPI.search({
          experience: experience || undefined,
          city: city || undefined,
          guests: guests || undefined,
          type: selectedType || undefined,
          max_price: maxPrice || undefined,
          amenities: selectedAmenities.length > 0 ? selectedAmenities.join(',') : undefined,
          limit: 24
        });
        setProperties(data.properties || []);
        setTotal(data.total || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [experience, city, guests, selectedType, maxPrice, selectedAmenities]);

  const toggleAmenity = (a) => {
    setSelectedAmenities(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  };

  const clearFilters = () => {
    setSelectedType('');
    setMaxPrice('');
    setSelectedAmenities([]);
  };

  const activeFilters = [
    experience && `Experience: ${experience}`,
    city && `City: ${city}`,
    guests && `Guests: ${guests}+`,
    selectedType && `Type: ${selectedType}`,
    maxPrice && `Max: ₹${maxPrice}`,
    ...selectedAmenities.map(a => a)
  ].filter(Boolean);

  return (
    <div data-testid="search-page" className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      {/* Search header */}
      <div className="bg-white border-b border-gray-100 pt-24 pb-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-5">
            <SearchWidget variant="compact" defaultExperience={experience || 'stay'} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-navy" data-testid="search-results-title">
                {loading ? 'Searching...' : `${total} properties found`}
                {city && <span className="text-gray-400 font-normal"> in {city}</span>}
              </h1>
              {activeFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {activeFilters.map(f => (
                    <span key={f} className="bg-gold/10 text-navy text-xs px-2.5 py-1 rounded-full capitalize">{f}</span>
                  ))}
                  <button onClick={clearFilters} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
                    <X size={12} /> Clear
                  </button>
                </div>
              )}
            </div>
            <button
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm font-medium text-navy border border-gray-200 px-4 py-2 rounded-xl hover:border-gold transition-colors"
            >
              <SlidersHorizontal size={15} /> Filters
              {selectedAmenities.length + (selectedType ? 1 : 0) + (maxPrice ? 1 : 0) > 0 && (
                <span className="bg-gold text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {selectedAmenities.length + (selectedType ? 1 : 0) + (maxPrice ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex gap-8">
          {/* Filters sidebar */}
          {showFilters && (
            <motion.aside
              data-testid="filters-sidebar"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="w-64 shrink-0"
            >
              <div className="bg-white rounded-2xl p-6 border border-gray-100 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-navy text-sm">Filters</h3>
                  <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-red-400">Reset</button>
                </div>

                {/* Property Type */}
                <div className="mb-6">
                  <label className="text-xs uppercase tracking-wider text-gray-400 font-medium block mb-3">Property Type</label>
                  <select
                    data-testid="filter-type"
                    value={selectedType}
                    onChange={e => setSelectedType(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-gold"
                  >
                    {TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>

                {/* Max Price */}
                <div className="mb-6">
                  <label className="text-xs uppercase tracking-wider text-gray-400 font-medium block mb-3">Max Price (₹)</label>
                  <input
                    data-testid="filter-max-price"
                    type="number"
                    placeholder="e.g. 50000"
                    value={maxPrice}
                    onChange={e => setMaxPrice(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-gold"
                  />
                </div>

                {/* Amenities */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-400 font-medium block mb-3">Amenities</label>
                  <div className="flex flex-wrap gap-2">
                    {AMENITY_OPTIONS.map(a => (
                      <button
                        key={a}
                        data-testid={`amenity-filter-${a}`}
                        onClick={() => toggleAmenity(a)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-colors duration-150 ${
                          selectedAmenities.includes(a)
                            ? 'bg-navy text-white border-navy'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gold'
                        }`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.aside>
          )}

          {/* Results */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl h-72 animate-pulse border border-gray-100" />
                ))}
              </div>
            ) : properties.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
              </div>
            ) : (
              <div data-testid="no-results" className="text-center py-24">
                <MapPin size={40} className="text-gray-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-navy mb-2">No properties found</h3>
                <p className="text-gray-400 text-sm mb-6">Try adjusting your filters or search in a different city.</p>
                <button onClick={clearFilters} className="text-sm text-gold hover:text-gold-dark font-medium">Clear all filters</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Search;
